import { Fragment, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { nanoid } from "nanoid";
import type {
  PremiseConfig,
  SchemeContent,
  SchemeInfoSection,
  SchemeKnobValues,
} from "@shared/types/foyer";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import { hasPromotionsLicense, hasStreamingTvLicense } from "@shared/products";
import { resolveLocalized } from "@shared/i18n";
import LivePreview from "../../components/template/LivePreview";
import TemplateSubEditor, {
  useTemplateApps,
} from "../../components/template/TemplateSubEditor";
import {
  ContentLanguageBar,
  localeStatus,
  readAt,
} from "../../components/template/LocalizedSectionsEditor";
import { ItemFormSurface } from "../../components/EditDrawer";
import {
  moveZone,
  type ScreenRow,
} from "../../components/template/screensModel";
import { PageHeader } from "../../components/PageHeader";
import {
  ActiveChoice,
  PanelHeading,
  PlacementChoice,
  StartNavLayout,
  startNavRoot,
} from "../../components/startnav/StartNavLayout";
import InfoSectionList, {
  ownKey,
  type ScreenEntry,
} from "../../components/startnav/InfoSectionList";
import OwnSectionEditor from "../../components/startnav/OwnSectionEditor";
import {
  WeatherFields,
  WifiFields,
} from "../../components/startnav/InfoFields";
import {
  effectiveBinding,
  menuZones,
  screenIdOf,
  standardInfoSections,
} from "../../components/startnav/menuItems";
import { tokens } from "../../theme";
import type {
  PremiseCommit,
  PremiseCommitMessages,
} from "../../context/PremiseWorkspace";
import {
  EditSectionCard,
  useCommitNext,
} from "../../components/template/EditSection";

/** The toast for one change: what happened, and what failed if it did. */
type Say = PremiseCommitMessages;

/**
 * Start & navigation → Start view → Information → Own sections
 * (NendaPortal_1 1689:14148) or → Standard sections (1748:20043).
 *
 * Information itself is a drawer on Start view; it links here. `own` edits the
 * operator's sections (`content.infoSections`); `standard` configures Wi-Fi,
 * Welcome message, Instagram and Weather (plus whatever else the template
 * files inside Information). List, editor, preview.
 *
 * Nothing waits for a page-level Save. Switches, placement and reordering save
 * the moment they change; an own section is added in a modal and edited in a
 * right-hand drawer whose Save writes it (picking it in the list opens it); the
 * Wi-Fi and Weather fields open in a drawer of their own. Every save toasts.
 * The middle column shows the selected section read-only.
 *
 * Selection lives in the URL: `?item=<screen id>` for a screen (old
 * `?item=wifi` links land on Wi-Fi), `?section=<id>` for an own section.
 */
export default function InformationPage({
  mode,
  premise,
  onCommit,
}: {
  mode: "own" | "standard";
  premise: PremiseConfig;
  /** Saves a change to the premise and toasts it (PremiseWorkspace `commit`). */
  onCommit: PremiseCommit;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const [params, setParams] = useSearchParams();
  // A place with no template is edited as the one its TVs show (as Start view).
  const binding = useMemo(() => effectiveBinding(premise), [premise]);
  const effective = useMemo<PremiseConfig>(
    () =>
      premise.scheme === binding ? premise : { ...premise, scheme: binding },
    [premise, binding]
  );
  const manifest = SCHEME_MANIFESTS[binding.key];
  const apps = useTemplateApps(effective);
  const commitNext = useCommitNext(premise, onCommit);
  // The editors below are handed `effective` (a place with no template is
  // edited as Nord), so an update they build assumes that binding exists. Make
  // it exist on the SAVED config too before the update runs on it.
  const commitOnEffective: PremiseCommit = (update, messages) =>
    onCommit((c) => update(c.scheme ? c : { ...c, scheme: binding }), messages);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // The own section in the modal / drawer: "new" = adding one.
  const [editing, setEditing] = useState<string | "new" | null>(null);

  const defaultLocale = premise.defaultLocale;
  const locales = useMemo(() => {
    const list = premise.supportedLocales?.length
      ? premise.supportedLocales
      : [defaultLocale];
    return [defaultLocale, ...list.filter((l) => l !== defaultLocale)];
  }, [premise.supportedLocales, defaultLocale]);
  const [activeLocale, setActiveLocale] = useState(defaultLocale);
  const locale = locales.includes(activeLocale) ? activeLocale : defaultLocale;

  const standardSections = standardInfoSections(t);
  const standardName = new Map(standardSections.map((s) => [s.id, s.name]));
  const information = t("menuItems.items.info.name");

  const crumbs = [
    startNavRoot(t),
    { label: t("startViewPage.title"), to: "/frontlayer/home" },
    { label: information, to: "/frontlayer/home?item=info" },
    {
      label:
        mode === "own"
          ? t("informationPage.ownSections")
          : t("informationPage.standardSections"),
    },
  ];

  if (!manifest) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 3 } }}>
        <PageHeader title={t("startViewPage.title")} />
        <Typography variant="body2" color="text.secondary">
          {t("startViewPage.unknownTemplate", { key: binding.key })}
        </Typography>
      </Box>
    );
  }

  const knobs = binding.knobs ?? {};
  const content = binding.content;
  const sections = content.infoSections;
  const where = premise.premiseName;
  // Each of these writes at once; `say` is the toast and what failed.
  const setBinding = (next: typeof binding, say: Say) =>
    commitNext({ ...premise, scheme: next }, say);
  const setKnobs = (next: SchemeKnobValues, say: Say) =>
    setBinding({ ...binding, knobs: next }, say);
  const setContent = (next: SchemeContent, say: Say) =>
    setBinding({ ...binding, content: next }, say);
  const setSections = (next: SchemeInfoSection[], say: Say) =>
    setContent({ ...content, infoSections: next }, say);

  const zones = menuZones(
    {
      manifest,
      knobs: binding.knobs,
      hasPromotions: hasPromotionsLicense(premise.products),
      hasStreamingTv: hasStreamingTvLicense(premise.products),
      appsCertified: apps.certifiedOn.size > 0,
      certificationNote: apps.certNote,
    },
    t
  );
  const idOf = (r: ScreenRow) => screenIdOf(manifest, r);
  const hasCoordinates =
    premise.address?.lat != null && premise.address?.lng != null;
  const entry = (r: ScreenRow): ScreenEntry => {
    const id = idOf(r);
    const on = r.fixedNote ? true : r.on;
    return {
      id,
      name: standardName.get(id) ?? r.label,
      status: !on
        ? { label: t("common:labels.off"), color: tokens.inkFaint }
        : id === "weather" && !hasCoordinates
          ? { label: t("informationPage.noLocation"), color: tokens.warning }
          : { label: t("common:labels.on"), color: tokens.success },
    };
  };
  const standardRows = standardSections
    .map((s) => zones.info.find((r) => idOf(r) === s.id))
    .filter((r): r is ScreenRow => !!r);
  const moreRows = zones.info.filter((r) => !standardName.has(idOf(r)));
  // The standard sections this template does NOT have (Atelier, Mosaic and
  // Spectrum have none): still listed, so the page says why they are missing.
  const missingStandard = standardSections.filter(
    (s) => !standardRows.some((r) => idOf(r) === s.id)
  );
  const missingEntries: ScreenEntry[] = missingStandard.map((s) => ({
    id: s.id,
    name: s.name,
    status: {
      label: t("informationPage.notIn", { name: manifest.name }),
      color: tokens.inkFaint,
    },
  }));
  const screenRows = [...standardRows, ...moreRows];

  const nameOf = (s: SchemeInfoSection) =>
    resolveLocalized(s.section, defaultLocale, defaultLocale).trim() ||
    t("informationPage.untitledSection");

  // What is selected: the URL's, else the first of this page's list.
  const wantSection = params.get("section");
  const wantItem = params.get("item");
  const selectedSection =
    mode === "own"
      ? (sections.find((s) => s.id === wantSection) ?? sections[0])
      : undefined;
  const selectedMissing =
    mode === "standard"
      ? (missingStandard.find((s) => s.id === wantItem) ??
        (screenRows.length === 0 ? missingStandard[0] : undefined))
      : undefined;
  const selectedRow =
    mode === "standard" && !selectedMissing
      ? (screenRows.find((r) => idOf(r) === wantItem) ?? screenRows[0])
      : undefined;
  const selectedKey = selectedSection
    ? ownKey(selectedSection.id)
    : (selectedMissing?.id ?? (selectedRow && idOf(selectedRow)));

  const select = (key: string) =>
    setParams(
      (prev) => {
        const q = new URLSearchParams(prev);
        q.delete("item");
        q.delete("section");
        if (key.startsWith("own:")) q.set("section", key.slice(4));
        else q.set("item", key);
        return q;
      },
      { replace: true }
    );

  // Picking an own section selects it (preview + summary) and opens it in the
  // drawer; a standard one is configured in the middle column as before.
  const pick = (key: string) => {
    select(key);
    if (key.startsWith("own:")) setEditing(key.slice(4));
  };
  const addSection = () => setEditing("new");
  const editingSection =
    editing === "new"
      ? { id: nanoid(6), section: "New section", items: [] }
      : sections.find((s) => s.id === editing);
  const saveSection = async (next: SchemeInfoSection) => {
    const label = nameOf(next);
    const adding = editing === "new";
    const ok = await setSections(
      adding
        ? [...sections, next]
        : sections.map((s) => (s.id === next.id ? next : s)),
      {
        success: adding
          ? t("informationPage.sectionAdded", { name: label, premise: where })
          : t("informationPage.sectionUpdated", { name: label }),
        failure: adding ? "addInfoSection" : "saveInfoSection",
        failureParams: { name: label },
      }
    );
    if (!ok) return false;
    if (adding) select(ownKey(next.id));
    // The surface closes itself (it slides out first, then clears `editing`).
    return true;
  };
  const deleteSelected = async () => {
    if (!selectedSection) return;
    const label = nameOf(selectedSection);
    const ok = await setSections(
      sections.filter((s) => s.id !== selectedSection.id),
      {
        success: t("informationPage.sectionDeleted", { name: label }),
        failure: "deleteInfoSection",
        failureParams: { name: label },
      }
    );
    if (!ok) return;
    setConfirmDelete(false);
    setParams(
      (prev) => {
        const q = new URLSearchParams(prev);
        q.delete("section");
        return q;
      },
      { replace: true }
    );
  };

  const selectedId = selectedRow ? idOf(selectedRow) : undefined;
  const selectedName = selectedSection
    ? nameOf(selectedSection)
    : selectedMissing
      ? selectedMissing.name
      : selectedRow
        ? (standardName.get(selectedId!) ?? selectedRow.label)
        : "";

  const editor = selectedMissing ? (
    <Alert
      severity="info"
      sx={{ fontSize: 14 }}
      data-testid="info-not-in-template"
    >
      {t("informationPage.notInTemplate", {
        template: manifest.name,
        section: selectedMissing.name,
      })}
      <Box sx={{ mt: 1.5 }}>
        <Button
          component={RouterLink}
          to="/frontlayer/appearance"
          variant="outlined"
          size="small"
          sx={{ textTransform: "none", fontWeight: "fontWeightSemibold" }}
          data-testid="info-change-template"
        >
          {t("informationPage.changeTemplate")}
        </Button>
      </Box>
    </Alert>
  ) : selectedSection ? (
    <>
      {locales.length > 1 && (
        <Box sx={{ mb: 3 }}>
          <ContentLanguageBar
            locales={locales}
            defaultLocale={defaultLocale}
            active={locale}
            statusOf={(code) => localeStatus(sections, code, defaultLocale)}
            onSelect={setActiveLocale}
          />
        </Box>
      )}
      <OwnSectionSummary
        section={selectedSection}
        locale={locale}
        defaultLocale={defaultLocale}
        onEdit={() => setEditing(selectedSection.id)}
      />
    </>
  ) : selectedRow ? (
    <>
      <ActiveChoice
        label={selectedName}
        title={t("startNavLayout.active")}
        on={selectedRow.fixedNote ? true : selectedRow.on}
        onChange={
          selectedRow.knobId
            ? (on) =>
                void setKnobs(
                  { ...knobs, [selectedRow.knobId!]: on },
                  {
                    success: on
                      ? t("informationPage.turnedOn", {
                          name: selectedName,
                          premise: where,
                        })
                      : t("informationPage.turnedOff", {
                          name: selectedName,
                          premise: where,
                        }),
                    failure: on ? "turnOnNamed" : "turnOffNamed",
                    failureParams: { name: selectedName },
                  }
                )
            : undefined
        }
        fixedNote={selectedRow.fixedNote}
        help={t("informationPage.activeHelp", { name: selectedName })}
      />
      {selectedRow.moveTo && (
        <PlacementChoice
          insideInfo
          onMove={() => {
            const patch = moveZone(
              manifest,
              knobs,
              selectedRow.key,
              selectedRow.moveTo!.orderKnobId
            );
            if (patch)
              void setKnobs(patch, {
                success: t("informationPage.movedOut", { name: selectedName }),
                failure: "moveNamed",
                failureParams: { name: selectedName },
              });
          }}
        />
      )}
      {selectedId === "wifi" || selectedId === "weather" ? (
        // Free-text fields: edited in a drawer, saved with its Save.
        <EditSectionCard
          premise={effective}
          onCommit={commitOnEffective}
          title={selectedName}
          description={t("informationPage.detailsDescription", {
            name: selectedName,
          })}
          success={t("informationPage.detailsUpdated", {
            name: selectedName,
            premise: where,
          })}
          failure="saveNamedFor"
          failureParams={{ item: selectedName, name: where }}
          data-testid={`info-fields-${selectedId}`}
        >
          {(local, setLocal) => {
            const b = effectiveBinding(local);
            const setLocalContent = (c: SchemeContent) =>
              setLocal({ ...local, scheme: { ...b, content: c } });
            return selectedId === "wifi" ? (
              <WifiFields content={b.content} onChange={setLocalContent} />
            ) : (
              <WeatherFields content={b.content} onChange={setLocalContent} />
            );
          }}
        </EditSectionCard>
      ) : selectedRow.sub ? (
        <TemplateSubEditor
          premise={effective}
          sub={selectedRow.sub}
          onCommit={commitOnEffective}
          apps={apps}
          stateBar={false}
        />
      ) : null}
    </>
  ) : (
    <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
      {mode === "own"
        ? t("informationPage.noOwnSection")
        : t("informationPage.noStandardSections")}
    </Typography>
  );

  return (
    <>
      <StartNavLayout
        crumbs={crumbs}
        pageTitle={t("startViewPage.title")}
        pageSubtitle={t("startViewPage.subtitle")}
        title={information}
        subtitle={t("informationPage.subtitle")}
        list={
          <InfoSectionList
            mode={mode}
            standard={[...standardRows.map(entry), ...missingEntries]}
            own={sections.map((s) => ({ id: s.id, name: nameOf(s) }))}
            more={moreRows.map(entry)}
            selected={selectedKey}
            onSelect={pick}
            onReorderOwn={(ids) =>
              void setSections(
                ids
                  .map((id) => sections.find((s) => s.id === id))
                  .filter((s): s is SchemeInfoSection => !!s),
                {
                  success: t("informationPage.reordered", { premise: where }),
                  failure: "reorderInfoSections",
                }
              )
            }
            onAddOwn={addSection}
          />
        }
        editor={
          <Box data-testid="info-editor">
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={2}
            >
              <PanelHeading
                title={
                  mode === "own"
                    ? t("informationPage.manageSection")
                    : t("startViewPage.manageNamed", { name: selectedName })
                }
                subtitle={
                  mode === "own"
                    ? t("informationPage.manageSubtitleOwn")
                    : t("informationPage.manageSubtitleStandard", {
                        name: selectedName,
                      })
                }
              />
              {selectedSection && (
                <Tooltip title={t("informationPage.deleteSection")}>
                  <IconButton
                    aria-label={t("informationPage.deleteNamed", {
                      name: nameOf(selectedSection),
                    })}
                    onClick={() => setConfirmDelete(true)}
                    sx={{ color: tokens.inkMuted }}
                    data-testid="info-delete-section"
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            {editor}
          </Box>
        }
        preview={
          <>
            <PanelHeading
              title={t("startNavLayout.preview")}
              subtitle={
                mode === "standard" && selectedName
                  ? t("informationPage.previewSubtitleNamed", {
                      name: selectedName,
                    })
                  : t("informationPage.previewSubtitle")
              }
            />
            <LivePreview
              premise={effective}
              // A standard section opens its own screen; an own section is a
              // card on the Information page itself.
              screen={
                selectedId && standardName.has(selectedId) ? selectedId : "info"
              }
              maxWidth={400}
              hideLabel
            />
          </>
        }
      />
      {editing !== null && editingSection && (
        <OwnSectionSurface
          key={editing === "new" ? "new" : editing}
          mode={editing === "new" ? "create" : "edit"}
          initial={editingSection}
          sections={sections}
          locales={locales}
          locale={locale}
          defaultLocale={defaultLocale}
          onLocale={setActiveLocale}
          onClose={() => setEditing(null)}
          onSave={saveSection}
        />
      )}
      <Dialog
        open={confirmDelete && !!selectedSection}
        onClose={() => setConfirmDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("informationPage.deleteDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("informationPage.deleteDialogText", {
              name: selectedSection ? nameOf(selectedSection) : "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDelete(false)}
            sx={{ textTransform: "none" }}
          >
            {t("common:buttons.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void deleteSelected()}
            sx={{ textTransform: "none" }}
            data-testid="info-delete-confirm"
          >
            {t("common:buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Add (modal) / edit (right drawer) one own section. It edits a local copy:
 * Add / Apply saves it and closes only when the save worked (a failed save
 * keeps what was typed); Cancel throws it away.
 */
function OwnSectionSurface({
  mode,
  initial,
  sections,
  locales,
  locale,
  defaultLocale,
  onLocale,
  onClose,
  onSave,
}: {
  mode: "create" | "edit";
  initial: SchemeInfoSection;
  /** Every section, so the language bar's status counts the edit in progress. */
  sections: SchemeInfoSection[];
  locales: string[];
  locale: string;
  defaultLocale: string;
  onLocale: (locale: string) => void;
  onClose: () => void;
  onSave: (next: SchemeInfoSection) => Promise<boolean>;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const [draft, setDraft] = useState<SchemeInfoSection>(initial);
  const [busy, setBusy] = useState(false);
  const withDraft =
    mode === "create"
      ? [...sections, draft]
      : sections.map((s) => (s.id === draft.id ? draft : s));
  return (
    <ItemFormSurface
      mode={mode}
      onClose={onClose}
      title={
        mode === "create"
          ? t("infoSectionList.addSection")
          : t("informationPage.editSection")
      }
      subtitle={t("informationPage.surfaceSubtitle")}
      maxWidth="md"
      drawerWidth={720}
      data-testid="info-section-form"
      busy={busy}
      actions={(dismiss) => (
        <>
          <Button
            onClick={() => dismiss()}
            disabled={busy}
            sx={{ textTransform: "none" }}
          >
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              let ok = false;
              try {
                ok = await onSave(draft);
              } finally {
                setBusy(false);
              }
              if (ok) dismiss();
            }}
            sx={{ textTransform: "none" }}
            data-testid="info-section-save"
          >
            {busy
              ? t("common:states.saving")
              : mode === "create"
                ? t("common:buttons.add")
                : t("common:buttons.apply")}
          </Button>
        </>
      )}
    >
      <Box sx={{ pt: 1 }}>
        {locales.length > 1 && (
          <Box sx={{ mb: 3 }}>
            <ContentLanguageBar
              locales={locales}
              defaultLocale={defaultLocale}
              active={locale}
              statusOf={(code) => localeStatus(withDraft, code, defaultLocale)}
              onSelect={onLocale}
            />
          </Box>
        )}
        <OwnSectionEditor
          section={draft}
          locale={locale}
          defaultLocale={defaultLocale}
          onChange={setDraft}
        />
      </Box>
    </ItemFormSurface>
  );
}

/** The selected own section, read-only, in the middle column. */
function OwnSectionSummary({
  section,
  locale,
  defaultLocale,
  onEdit,
}: {
  section: SchemeInfoSection;
  locale: string;
  defaultLocale: string;
  onEdit: () => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const text = (v: SchemeInfoSection["section"] | undefined) =>
    readAt(v, locale, defaultLocale).trim();
  const muted = { fontSize: 14, color: tokens.inkMuted };
  return (
    <Box data-testid="own-section-summary">
      <Typography
        variant="meta"
        sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
      >
        {t("common:labels.title")}
      </Typography>
      <Typography
        sx={{
          mt: 0.5,
          mb: 3,
          typography: "titleSm",
          fontWeight: "fontWeightSemibold",
        }}
        data-testid="own-section-summary-title"
      >
        {text(section.section) || (
          <Box component="span" sx={{ ...muted, fontStyle: "italic" }}>
            {t("informationPage.notTranslated")}
          </Box>
        )}
      </Typography>
      {section.embedUrl ? (
        <>
          <Typography
            variant="meta"
            sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
          >
            {t("informationPage.embeddedPage")}
          </Typography>
          <Typography sx={{ ...muted, mt: 0.5, wordBreak: "break-all" }}>
            {section.embedUrl}
          </Typography>
        </>
      ) : (
        <>
          <Typography
            variant="meta"
            sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
          >
            {t("ownSectionEditor.rows", { count: section.items.length })}
          </Typography>
          {section.items.length === 0 ? (
            <Typography sx={{ ...muted, mt: 0.5 }}>
              {t("informationPage.noRows")}
            </Typography>
          ) : (
            <Box
              component="dl"
              sx={{
                mt: 1,
                mb: 0,
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)",
                gap: "8px 16px",
                typography: "body2",
                "& dd": { m: 0 },
              }}
            >
              {section.items.map((it, j) => (
                <Fragment key={j}>
                  <Box component="dt" sx={{ fontWeight: "fontWeightSemibold" }}>
                    {text(it.label) || "-"}
                  </Box>
                  <Box component="dd" sx={{ color: tokens.inkMuted }}>
                    {text(it.value) || "-"}
                  </Box>
                </Fragment>
              ))}
            </Box>
          )}
        </>
      )}
      <Button
        variant="outlined"
        startIcon={<EditOutlinedIcon />}
        onClick={onEdit}
        sx={{ mt: 3, textTransform: "none", fontWeight: "fontWeightSemibold" }}
        data-testid="info-edit-section"
      >
        {t("informationPage.editSection")}
      </Button>
    </Box>
  );
}
